<template>
  <div class="news-page">
    <Navbar />

    <!-- Breadcrumb Area -->
    <div class="breadcrumb-container">
      <div class="breadcrumb-content">
        <span>{{ t("activities.home") }}</span> &gt;
        <span>{{ t("activities.title") }}</span>
      </div>
    </div>

    <!-- Banner Area -->
    <div class="banner-area">
      <div class="banner-container">
        <div class="banner-content">
          <h2 class="banner-title">{{ t("activities.title") }}</h2>
        </div>
      </div>
    </div>

    <!-- 主要內容區塊錨點 (位於 Banner 之下) -->
    <div id="main-content" tabindex="-1" class="main-content-anchor">
      <a
        id="accesskey-c"
        href="#accesskey-c"
        accesskey="C"
        class="accesskey-brick sr-only-focusable"
        title="主要內容區塊，快捷鍵：Alt+C"
        aria-label="主要內容區塊"
      >:::</a>
    </div>

    <!-- Tabs Navigation -->
    <div class="tabs-container" :class="{ 'is-sticky': isSticky }">
      <div class="tabs-wrapper">
        <div
          class="tab-item"
          :class="{ active: activeTab === 'announcement' }"
          @click="scrollToSection('announcement')"
        >
          {{ t("activities.announcement") }}
        </div>
        <div
          class="tab-item"
          :class="{ active: activeTab === 'activity' }"
          @click="scrollToSection('activity')"
        >
          {{ t("activities.activity") }}
        </div>
      </div>
    </div>

    <div class="content-container">
      <!-- 公告區塊 -->
      <section id="announcement" class="section-block">
        <h3 class="section-heading">{{ t("activities.announcement") }}</h3>
        <div class="divider"></div>

        <div class="announcement-list">
          <div
            v-for="item in announcements"
            :key="item.id"
            class="announcement-item"
          >
            <div class="announcement-date">
              <router-link :to="'/activities/' + item.id">
                {{ formatDate(item.publish_at) }}
              </router-link>
            </div>
            <div class="announcement-content">
              <h4 class="announcement-title">
                <router-link :to="'/activities/' + item.id">
                  {{ getTitle(item) }}
                </router-link>
              </h4>
              <router-link :to="'/activities/' + item.id" class="read-more">
                {{ t("activities.read_more") }} <span class="arrow">⇀</span>
              </router-link>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper">
          <a-pagination
            v-model:current="announcementPage"
            :total="totalAnnouncements"
            show-less-items
            @change="fetchAnnouncements"
          />
        </div>
      </section>

      <!-- 活動區塊 -->
      <section id="activity" class="section-block">
        <h3 class="section-heading">{{ t("activities.activity") }}</h3>
        <div class="divider"></div>

        <div class="activity-grid">
          <div v-for="item in activities" :key="item.id" class="activity-card">
            <div class="activity-image">
              <router-link :to="'/activities/' + item.id">
                <img :src="getImage(item)" :alt="getTitle(item)" />
              </router-link>
            </div>
            <div class="activity-info">
              <h4 class="activity-title">
                <router-link :to="'/activities/' + item.id">
                  {{ getTitle(item) }}
                </router-link>
              </h4>

              <div class="info-row">
                <svg
                  class="icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19 3H18V1H16V3H8V1H6V3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V8H19V19Z"
                    fill="#000"
                  />
                </svg>
                <span
                  >{{ formatDate(item.start_date) }} -
                  {{ formatDate(item.end_date) }}</span
                >
              </div>
              <div class="info-row">
                <!-- 下午三點的時鐘SVG -->
                <svg
                  class="icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <!-- 外框 -->
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#000"
                    stroke-width="2"
                  />
                  <!-- 12點刻度 -->
                  <line
                    x1="12"
                    y1="2"
                    x2="12"
                    y2="4"
                    stroke="#000"
                    stroke-width="2"
                  />
                  <!-- 3點刻度 -->
                  <line
                    x1="22"
                    y1="12"
                    x2="20"
                    y2="12"
                    stroke="#000"
                    stroke-width="2"
                  />
                  <!-- 6點刻度 -->
                  <line
                    x1="12"
                    y1="22"
                    x2="12"
                    y2="20"
                    stroke="#000"
                    stroke-width="2"
                  />
                  <!-- 9點刻度 -->
                  <line
                    x1="2"
                    y1="12"
                    x2="4"
                    y2="12"
                    stroke="#000"
                    stroke-width="2"
                  />
                  <!-- 時針指向3 -->
                  <line
                    x1="12"
                    y1="12"
                    x2="16"
                    y2="12"
                    stroke="#000"
                    stroke-width="2"
                  />
                  <!-- 分針指向12 -->
                  <line
                    x1="12"
                    y1="12"
                    x2="12"
                    y2="6"
                    stroke="#000"
                    stroke-width="1.5"
                  />
                  <!-- 中心點 -->
                  <circle cx="12" cy="12" r="1.5" fill="#000" />
                </svg>
                <span>{{ formatTime(item.start_time, item.end_time) }}</span>
              </div>
              <div class="info-row">
                <svg
                  class="icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C8.1 2 5 5.1 5 9C5 14.3 12 22 12 22C12 22 19 14.3 19 9C19 5.1 15.9 2 12 2ZM12 11.5C10.6 11.5 9.5 10.4 9.5 9C9.5 7.6 10.6 6.5 12 6.5C13.4 6.5 14.5 7.6 14.5 9C14.5 10.4 13.4 11.5 12 11.5Z"
                    fill="#000"
                  />
                </svg>
                <span>{{ getLocation(item) }}</span>
              </div>

              <div class="activity-actions">
                <template v-if="getButtonStatus(item) === 'not_open'">
                  <a-button disabled class="action-btn disabled-btn">{{
                    t("activities.not_open_yet")
                  }}</a-button>
                </template>
                <template v-else-if="getButtonStatus(item) === 'ended'">
                  <a-button disabled class="action-btn disabled-btn">{{
                    t("activities.ended")
                  }}</a-button>
                </template>
                <template
                  v-else-if="getButtonStatus(item) === 'registration_closed'"
                >
                  <a-button disabled class="action-btn disabled-btn">{{
                    t("activities.registration_closed")
                  }}</a-button>
                </template>
                <template v-else>
                  <a-button
                    type="primary"
                    class="action-btn primary-btn"
                    :href="item.reg_link"
                    target="_blank"
                    >{{ t("activities.register") }}</a-button
                  >
                </template>
                <router-link
                  :to="'/activities/' + item.id"
                  custom
                  v-slot="{ navigate }"
                >
                  <a-button class="action-btn outline-btn" @click="navigate">{{
                    t("activities.more_info")
                  }}</a-button>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper">
          <a-pagination
            v-model:current="activityPage"
            :total="totalActivities"
            show-less-items
            @change="fetchActivities"
          />
        </div>
      </section>
    </div>

    <FooterSection />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import Navbar from "@/components/Navbar.vue";
import FooterSection from "@/components/FooterSection.vue";
import { useLayoutStore } from "@/stores/layout";
import { activitiesAPI } from "@/services/api.js";

// i18n
const layoutStore = useLayoutStore();
const { t, locale } = useI18n();

// 狀態
const activeTab = ref("announcement");
const isSticky = ref(false);
const announcementPage = ref(1);
const activityPage = ref(1);
const announcements = ref([]);
const activities = ref([]);
const totalAnnouncements = ref(0);
const totalActivities = ref(0);
const loading = ref(false);

const pageSize = 10;

const pad2 = (n) => String(n).padStart(2, "0");
const getNowDateTimeString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(
    d.getDate()
  )} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

const sortByPublishAtDesc = (a, b) =>
  String(b?.publish_at || "").localeCompare(String(a?.publish_at || ""));

const allAnnouncements = ref([]);
const allActivities = ref([]);

const paginate = (items, page) => {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
};

const fetchAnnouncements = (page = announcementPage.value) => {
  announcementPage.value = page;
  totalAnnouncements.value = allAnnouncements.value.length;
  announcements.value = paginate(allAnnouncements.value, announcementPage.value);
};

const fetchActivities = (page = activityPage.value) => {
  activityPage.value = page;
  totalActivities.value = allActivities.value.length;
  activities.value = paginate(allActivities.value, activityPage.value);
};

const refreshData = async () => {
  loading.value = true;
  try {
    const [annRes, actRes] = await Promise.all([
      activitiesAPI.list({ type: "notice" }),
      activitiesAPI.list({ type: "activities" }),
    ]);

    allAnnouncements.value = (annRes.data ?? []).slice().sort(sortByPublishAtDesc);
    allActivities.value = (actRes.data ?? []).slice().sort(sortByPublishAtDesc);

    fetchAnnouncements(1);
    fetchActivities(1);
  } catch (e) {
    allAnnouncements.value = [];
    allActivities.value = [];
    fetchAnnouncements(1);
    fetchActivities(1);
  } finally {
    loading.value = false;
  }
};

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return "";
  return String(dateString).split(" ")[0].replace(/-/g, ".");
};

// 格式化時間
const formatTime = (startTime, endTime) => {
  if (!startTime || !endTime) return "";
  return `${startTime.slice(0, 5)} - ${endTime.slice(0, 5)}`;
};

// 獲取標題（i18n）
const getTitle = (item) => {
  if (locale.value === "en-US") {
    return item.title_en || item.title;
  }
  return item.title;
};

// 獲取地點（i18n）
const getLocation = (item) => {
  if (locale.value === "en-US") {
    return item.locate_en || item.locate;
  }
  return item.locate;
};

// 獲取圖片（i18n）
const getImage = (item) => {
  if (locale.value === "en-US") {
    return item.featured_image_en || item.featured_image;
  }
  return item.featured_image;
};

// 取得按鈕狀態
const getButtonStatus = (item) => {
  const now = getNowDateTimeString();

  // 1. 尚未開放報名
  if (!item.is_registration_open) return "not_open";

  // 2. 活動已結束 (或是活動已開始)
  // 根據需求表：活動開始日期「已過去」 -> 活動已結束
  if (item.start_date) {
    const startTimeStr = item.start_time || "00:00:00";
    const startDateTimeStr = `${item.start_date} ${startTimeStr}`;
    if (now > startDateTimeStr) {
      return "ended";
    }
  }

  // 3. 報名已截止
  // 根據需求表：活動開始日期「尚未到」且 報名截止時間「已過去」
  if (item.registration_end_time) {
    // 這裡只須判斷截止時間，因為如果活動已開始，上面已經 return "ended" 了
    if (now > item.registration_end_time) {
      return "registration_closed";
    }
  }

  // 4. 前往報名
  return "register";
};

// 檢查活動是否已結束 (保留給其他用途，但按鈕邏輯已移至 getButtonStatus)
const isActivityEnded = (item) => {
  if (!item.end_date) return false;

  // 組合結束時間，如果沒有 end_time 則預設為當天最後一秒
  const endTimeStr = item.end_time || "23:59:59";
  const endDateTimeStr = `${item.end_date} ${endTimeStr}`;
  const now = getNowDateTimeString();

  return now > endDateTimeStr;
};

// 滾動處理
const scrollToSection = (sectionId) => {
  activeTab.value = sectionId;
  const element = document.getElementById(sectionId);
  if (element) {
    const offset = 100; // 扣除 header 高度
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

// 監聽滾動以切換 active tab
const handleScroll = () => {
  const scrollPosition = window.scrollY + 150;

  const announcementSection = document.getElementById("announcement");
  const activitySection = document.getElementById("activity");

  if (announcementSection && activitySection) {
    if (scrollPosition >= activitySection.offsetTop) {
      activeTab.value = "activity";
    } else if (scrollPosition >= announcementSection.offsetTop) {
      activeTab.value = "announcement";
    }
  }
};

onMounted(() => {
  refreshData();
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped lang="scss">
.news-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Breadcrumb 樣式 */
.breadcrumb-container {
  // margin-top: 120px;
  // enabled announcement-banner
  margin-top: v-bind("layoutStore.pageMarginTop");

  @media (max-width: 768px) {
    margin-top: v-bind('layoutStore.showAnnouncementBanner ? "160px" : "55px"');
  }

  background: white;
  padding: 20px 0;
  border-bottom: 1px solid #e0e0e0;
}

.breadcrumb-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  font-size: 14px;
  color: #333;

  span {
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: var(--color-primary);
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 0 16px;
  }
}

/* Banner 樣式 - 參考 Terms.vue 但加入背景圖效果 */
.banner-area {
  background: linear-gradient(rgba(2, 115, 112, 0.1), rgba(2, 115, 112, 0.1)),
    url("/news_banner.webp") center/cover no-repeat;
  width: 100%;
  height: 250px;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    height: 180px;
  }
}

.banner-container {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0 20px;
}

.banner-content {
  color: white;
}

.banner-title {
  font-size: 48px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 32px;
  }
}

/* Tabs 樣式 */
.tabs-container {
  background: white;
  position: sticky;
  top: 55px; /* Navbar height */
  z-index: 100;
}

.tabs-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  gap: 0;
}

.tab-item {
  padding: 20px 20px;
  font-size: 18px;
  color: #666;
  cursor: pointer;
  position: relative;
  font-weight: 500;
  transition: all 0.3s;
  border-bottom: 2px solid #696969;

  &:hover {
    color: var(--color-primary);
  }

  &.active {
    color: var(--color-primary);
    font-weight: 700;
    border-bottom: 2px solid var(--color-primary);
  }
}

/* 主要內容區塊 */
.content-container {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 60px 20px;
  flex: 1;
}

.section-block {
  margin-bottom: 80px;
  scroll-margin-top: 160px; /* 預留 sticky header 空間 */
}

.section-heading {
  font-size: 36px;
  color: var(--color-primary);
  margin-bottom: 10px;
  font-weight: 700;
}

.divider {
  height: 2px;
  background-color: var(--color-primary);
  width: 100%;
  margin-bottom: 40px;
}

/* 公告列表樣式 */
.announcement-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.announcement-item {
  display: flex;
  padding: 30px 0;
  border-bottom: 1px solid #eee;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px 0;
  }
}

.announcement-date {
  width: 100px;
  font-size: 16px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
  text-align: center;

  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 10px;
    font-size: 14px;
    text-align: left;
  }
}

.announcement-content {
  flex: 1;
  padding-left: 10px;
  border-left: 1px solid var(--color-primary);
  @media (max-width: 768px) {
    padding-left: 0;
    border-left: none;
  }
}

.announcement-title {
  font-size: 18px;
  color: #333;
  margin-bottom: 15px;
  line-height: 1.6;
  font-weight: 700;
}

.read-more {
  display: inline-flex;
  align-items: center;
  color: #333;
  text-decoration: none;
  font-size: 14px;
  border-bottom: 1px solid #333;
  padding-bottom: 2px;
  float: right;

  .arrow {
    margin-left: 5px;
    transition: transform 0.3s;
    // font-size: 20px;
  }

  &:hover .arrow {
    transform: translateX(5px);
  }
}

/* 活動卡片樣式 */
.activity-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.activity-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
}

.activity-image {
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    transition: transform 0.5s;
  }

  &:hover img {
    transform: scale(1.05);
  }
}

.activity-info {
  padding: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.activity-title {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  border-bottom: 1px solid #ccc;
  padding-bottom: 10px;
  margin-bottom: 20px;
  line-height: 1.4;
}

.info-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  color: #666;
  font-size: 15px;

  .icon {
    margin-right: 10px;
    min-width: 20px;
  }
}

.activity-actions {
  margin-top: auto;
  padding-top: 24px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.action-btn {
  height: 44px;
  border-radius: 22px;
  font-size: 16px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.primary-btn {
  background-color: var(--color-primary);
  border-color: var(--color-primary);

  &:hover {
    background-color: var(--color-primary-hover);
    border-color: var(--color-primary-hover);
  }
}

.outline-btn {
  border-color: var(--color-primary);
  color: var(--color-primary);

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background-color: rgba(2, 115, 112, 0.08);
  }
}

.disabled-btn {
  background-color: #d9d9d9;
  border-color: #d9d9d9;
  color: rgba(0, 0, 0, 0.25);
  cursor: not-allowed;

  &:hover {
    background-color: #d9d9d9;
    border-color: #d9d9d9;
    color: rgba(0, 0, 0, 0.25);
  }
}

/* 分頁器 */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}

/* Ant Design Override */
:deep(.ant-pagination-item-active) {
  border-color: var(--color-primary);

  a {
    color: var(--color-primary);
  }
}
</style>
